import getFrontConfig from '../getFrontConfig';

const type = require('./type');

const FRONT_CONFIG = getFrontConfig();

const DOUBLELOOK_REGEX = new RegExp(FRONT_CONFIG.doubleLook.DOUBLELOOK_REGEX_STRING);

/**
 * Return doubleLook code from pid
 *
 * Examples:
 * 201BR48P3.T1000_DL1M => DL1
 * 201BR48P3.T1000 => null
 * 201BR48P3 => null
 *
 * @param {string} pid ID of a variant or variation group
 * @return {string|null} Double look code
 */
function getDoubleLookCodeFromPid(pid) {
  const result = getDoubleLookValueFromPid(pid);
  if (type.isNumber(parseFloat(result))) return `DL${result}`;

  return result;
}

/**
 * Return PID without size but including color & dl variations
 *
 * Examples:
 * 201BR48P3.T1000_DL1M => 201BR48P3.T1000_DL1
 * 201BR48P3.T1000M => 201BR48P3.T1000
 * 201BR48P3.T1000 => 201BR48P3.T1000
 * 201BR48P3 => 201BR48P3
 *
 * @param {string} pid ID of a variant or variation group
 * @return {string} PID without size but including color & dl variations
 */
function getDoubleLookPIDWithoutSize(pid) {
  if (!pid) return pid;
  const matches = pid.match(/.+\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})(_DL\d)?/);
  const match = matches?.length > 0 && matches[0];
  if (match) {
    return match;
  }
  return pid;
}

/**
 * Return byl_doublelook from pid
 *
 * Examples:
 * 201BR48P3.T1000_DL1M => 1
 * 201BR48P3.T1000 => null
 * 201BR48P3 => null
 *
 * @param {string} pid ID of a variant or variation group
 * @return {string|null} Double look value
 */
function getDoubleLookValueFromPid(pid) {
  let result = null;
  if (!pid) return result;

  const matches = pid.match(/\.([a-zA-Z][0-9]{4}|\d{1}\w{1}\d{3})_DL(\d)/); // Slightly modified DOUBLELOOK_REGEX
  const match = matches && matches.length > 2 && matches[2];
  if (match) {
    // Return match (ex: '1' from '201BR48P3.T1000_DL1')
    result = match;
  }
  return result;
}

/** Remove '_DL' from the PID to get the originalProductId if it is a DL product
 *
 * Examples:
 * 201BR48P3.T1000_DL1 => 201BR48P3.T1000
 * 201BR48P3.T1000_DL3M => 201BR48P3.T1000
 * 201BR48P3.T1000 => 201BR48P3.T1000
 *
 * @param  {string} productId - Product ID
 * @returns {string} Original productId
 */
function getOriginalPID(productId) {
  return productId.replace(DOUBLELOOK_REGEX, '');
}

/** Remove '_DL' from the productUrl
 *
 * Examples:
 * ../blue-fruits-swimsuit-201BABQ12.I6480_DL1.html => ../blue-fruits-swimsuit-201BABQ12.I6480.html
 * ../blue-fruits-swimsuit-201BABQ12.I6480.html => ../blue-fruits-swimsuit-201BABQ12.I6480.html
 *
 * @param  {string} productUrl - Product Url
 * @returns {string} Original productUrl
 */
function getOriginalProductUrl(productUrl) {
  return productUrl.replace(DOUBLELOOK_REGEX, '');
}

/** Return true if the productId belongs to a DL product
 * @param  {string} productId - Product ID
 * @returns {boolean} true if the productId is from a DL, false otherwise
 */
function isDoubleLook(productId) {
  return productId.match(DOUBLELOOK_REGEX) !== null;
}

export {
  getDoubleLookCodeFromPid,
  getDoubleLookPIDWithoutSize,
  getDoubleLookValueFromPid,
  getOriginalPID,
  getOriginalProductUrl,
  isDoubleLook,
};
